import { UntypedFormControl } from '@angular/forms';
import { ReplaySubject, Subject } from 'rxjs';
import { takeUntil } from 'rxjs/operators';

declare var $: any;

/**
 * Every `mat-select`/`mat-autocomplete`/etc. panel is a CDK overlay appended
 * to <body>, outside any Bootstrap `.modal` element. Bootstrap 4's modal
 * focus-trap (`_enforceFocus`, vendored at src/assets/lib/js/bootstrap.min.js)
 * force-refocuses the modal whenever focus lands on anything it doesn't
 * contain — so a search `<input>` inside a CDK overlay panel opened from
 * within a Bootstrap modal can never keep keyboard focus.
 *
 * This patches Bootstrap's `_enforceFocus` once, app-wide, to also treat
 * `.cdk-overlay-container` as "contained" — the minimal generalization of
 * Bootstrap's own containment check. Applied lazily (first `SelectSearchFilter`
 * constructed) so pages that never use dropdown search never pay for it, and
 * it fixes every current and future modal automatically — no per-component
 * `shown.bs.modal` workaround needed.
 */
let bootstrapModalFocusTrapPatched = false;
function patchBootstrapModalFocusTrap(): void {
  if (bootstrapModalFocusTrapPatched) {
    return;
  }
  const ModalCtor = typeof $ !== 'undefined' && $.fn?.modal?.Constructor;
  if (!ModalCtor) {
    return;
  }
  bootstrapModalFocusTrapPatched = true;

  ModalCtor.prototype._enforceFocus = function (this: any): void {
    const modal = this;
    $(document).off('focusin.bs.modal').on('focusin.bs.modal', (event: any) => {
      const target = event.target;
      if (
        document !== target &&
        modal._element !== target &&
        $(modal._element).has(target).length === 0 &&
        !(target.closest && target.closest('.cdk-overlay-container'))
      ) {
        modal._element.focus();
      }
    });
  };
}

/** A single field to match on: a property name, or an extractor for anything not shaped as `item[field]` (e.g. a bilingual name picked by current language). */
type MatchField<T> = keyof T | ((item: T) => string);

/** Translation keys under the shared `search-filter` i18n namespace (see en.json/ar.json) — never a per-view key, so the same label is reused everywhere a search box shows the same kind of prompt. */
export interface SelectSearchFilterOptions {
  placeholderKey?: string;
  noResultsKey?: string;
}

/**
 * `ngx-mat-select-search` only renders the search box inside a `mat-select`
 * panel — it has no idea what your options look like, so matching typed text
 * against a field is always application code. This class is that matching
 * logic, written once, so a searchable `mat-select` needs only:
 *
 *   gateSearch = new SelectSearchFilter<Gate>('name');
 *   // template:
 *   //   [formControl]="gateSearch.filterCtrl"
 *   //   [placeholderLabel]="gateSearch.placeholderKey | translate"
 *   //   [noEntriesFoundLabel]="gateSearch.noResultsKey | translate"
 *   //   *ngFor="let g of gateSearch.filtered$ | async"
 *   this.gateSearch.setSource(this.gates);   // whenever the options array loads/changes
 *   this.gateSearch.destroy();               // in the host's ngOnDestroy
 *
 * Pass an array to search across multiple fields at once (matches if ANY of
 * them contains the typed text), e.g. name-or-national-ID:
 *
 *   studentSearch = new SelectSearchFilter<Student>(['name', 'national_id'], {
 *     placeholderKey: 'search-filter.search-by-name-or-id',
 *   });
 *
 * `placeholderKey`/`noResultsKey` default to the generic `search-filter.search` /
 * `search-filter.no-results-found` keys — only pass your own when the field
 * needs to tell the user which criteria it searches by.
 */
export class SelectSearchFilter<T = any> {
  readonly filterCtrl = new UntypedFormControl('');
  readonly filtered$ = new ReplaySubject<T[]>(1);
  readonly placeholderKey: string;
  readonly noResultsKey: string;

  private readonly matchFields: MatchField<T>[];
  private source: T[] = [];
  private readonly destroyed$ = new Subject<void>();

  constructor(matchField: MatchField<T> | MatchField<T>[], options: SelectSearchFilterOptions = {}) {
    patchBootstrapModalFocusTrap();
    this.matchFields = Array.isArray(matchField) ? matchField : [matchField];
    this.placeholderKey = options.placeholderKey ?? 'search-filter.search';
    this.noResultsKey = options.noResultsKey ?? 'search-filter.no-results-found';
    this.filterCtrl.valueChanges
      .pipe(takeUntil(this.destroyed$))
      .subscribe(() => this.applyFilter());
  }

  /** Call whenever the underlying options array changes (initial load, a dependent grade→class change, etc). */
  setSource(source: T[]): void {
    this.source = source ?? [];
    this.applyFilter();
  }

  /** Clears the typed search text — e.g. when a dependent list (class options) is reset after its parent (grade) changes. */
  reset(): void {
    this.filterCtrl.setValue('', { emitEvent: false });
    this.applyFilter();
  }

  /** Stops listening for typed input. Call from the host component's ngOnDestroy. */
  destroy(): void {
    this.destroyed$.next();
    this.destroyed$.complete();
  }

  private applyFilter(): void {
    const search = (this.filterCtrl.value ?? '').toString().trim().toLowerCase();
    if (!search) {
      this.filtered$.next(this.source.slice());
      return;
    }
    this.filtered$.next(
      this.source.filter(item => this.matchFields.some(field => this.extract(item, field).toLowerCase().includes(search)))
    );
  }

  private extract(item: T, field: MatchField<T>): string {
    const value = typeof field === 'function' ? field(item) : (item as any)[field];
    return value ?? '';
  }
}
